/**
 * Provision Labs Dialog
 * Dialog for instructors to provision N lab instances
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, Plus, Minus } from 'lucide-react';

interface ProvisionLabsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className: string;
  currentEnrollments: number;
  currentProvisionedCount: number;
  maxStudents: number;
  onPrime: (count: number) => Promise<void>;
}

export function ProvisionLabsDialog({
  open,
  onOpenChange,
  className,
  currentEnrollments,
  currentProvisionedCount,
  maxStudents,
  onPrime,
}: ProvisionLabsDialogProps) {
  // Suggest: enrolled students + 2 buffer, or at least 1
  const suggestedCount = Math.max(1, currentEnrollments + 2 - currentProvisionedCount);
  const remainingCapacity = Math.max(0, maxStudents - currentProvisionedCount);

  const [count, setCount] = useState(Math.min(suggestedCount, remainingCapacity, 50));
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      const newSuggested = Math.min(
        Math.max(1, currentEnrollments + 2 - currentProvisionedCount),
        remainingCapacity,
        50
      );
      setCount(Math.max(1, newSuggested));
      setError(null);
    }
  }, [open, currentEnrollments, currentProvisionedCount, remainingCapacity]);

  const handleIncrement = () => {
    setCount((prev) => Math.min(prev + 1, 50, remainingCapacity));
  };

  const handleDecrement = () => {
    setCount((prev) => Math.max(prev - 1, 1));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      setCount(Math.min(Math.max(value, 1), 50, remainingCapacity));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (count < 1 || count > 50) {
      setError('Count must be between 1 and 50');
      return;
    }

    if (count > remainingCapacity) {
      setError(`Cannot provision more than ${remainingCapacity} additional instances (max students: ${maxStudents})`);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onPrime(count);
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to provision labs');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showCapacityWarning = count > remainingCapacity && remainingCapacity > 0;
  const atCapacity = remainingCapacity <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Provision Lab Instances</DialogTitle>
          <DialogDescription>
            Create new lab instances for <span className="font-medium">{className}</span>.
            Each instance includes all lab types configured for this class.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {atCapacity && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Class is at maximum capacity ({maxStudents} students).
                Increase max students to provision more instances.
              </AlertDescription>
            </Alert>
          )}

          {!atCapacity && (
            <>
              {/* Current status */}
              <div className="rounded-lg bg-muted p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Currently enrolled:</span>
                  <span className="font-medium">{currentEnrollments} students</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Already provisioned:</span>
                  <span className="font-medium">{currentProvisionedCount} instances</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Remaining capacity:</span>
                  <span className="font-medium">{remainingCapacity} instances</span>
                </div>
              </div>

              {/* Suggestion */}
              {suggestedCount > 0 && (
                <p className="text-sm text-muted-foreground">
                  Suggested: <span className="font-medium">{Math.min(suggestedCount, remainingCapacity)}</span> instances
                  ({currentEnrollments} enrolled + 2 buffer{currentProvisionedCount > 0 ? ` - ${currentProvisionedCount} existing` : ''})
                </p>
              )}

              {/* Count input */}
              <div className="space-y-2">
                <Label htmlFor="count">Number of Instances to Provision</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDecrement}
                    disabled={count <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    id="count"
                    type="number"
                    min={1}
                    max={Math.min(50, remainingCapacity)}
                    value={count}
                    onChange={handleInputChange}
                    className="w-24 text-center"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleIncrement}
                    disabled={count >= 50 || count >= remainingCapacity}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {showCapacityWarning && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This would exceed the class maximum of {maxStudents} students.
                    Maximum additional instances: {remainingCapacity}
                  </AlertDescription>
                </Alert>
              )}

              {/* Provisioning info */}
              <p className="text-xs text-muted-foreground">
                Provisioning takes 60-90 seconds per instance. You can monitor progress in real-time.
              </p>
            </>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || atCapacity}>
              {isSubmitting ? 'Provisioning...' : `Provision ${count} Instance${count !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
